import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useMemo } from 'react';
import type { NoteData } from 'components/Notes/types';
import type { Note } from 'pages/ValuationsAllocation/types';

interface UseValuationNotesCacheParams {
  cachedNotes?: Note[] | null;
  notes: NoteData[] | null;
  notesHasChanged: boolean;
  panelId?: string;
  setCachedNotes?: Dispatch<SetStateAction<Note[]>> | null;
  setNotes: (notes: NoteData[]) => void;
  setNotesHasChanged: Dispatch<SetStateAction<boolean>>;
}

export const useValuationNotesCache = ({
  cachedNotes,
  notes,
  notesHasChanged,
  panelId,
  setCachedNotes,
  setNotes,
  setNotesHasChanged,
}: UseValuationNotesCacheParams) => {
  const cachedNote = useMemo(
    () => (panelId ? cachedNotes?.find(noteEntry => noteEntry.panelId === panelId) : undefined),
    [cachedNotes, panelId]
  );
  const cachedPanelNotes = cachedNote?.notes ?? null;

  // Rehydrate local approach notes when returning to a tab before the valuation has been saved.
  useEffect(() => {
    if (!panelId || notes !== null || !cachedPanelNotes) {
      return;
    }

    setNotes(cachedPanelNotes);
    setNotesHasChanged(Boolean(cachedNote?.notesHasChanged));
  }, [cachedNote?.notesHasChanged, cachedPanelNotes, notes, panelId, setNotes, setNotesHasChanged]);

  // Keep the parent valuation cache current so unmounted approach tabs do not lose drafts.
  useEffect(() => {
    if (!panelId || !setCachedNotes || notes === null) {
      return;
    }

    setCachedNotes(previousNotes => {
      const currentEntry = previousNotes.find(noteEntry => noteEntry.panelId === panelId);
      if (currentEntry?.notes === notes && currentEntry.notesHasChanged === notesHasChanged) {
        return previousNotes;
      }

      const otherNotes = previousNotes.filter(noteEntry => noteEntry.panelId !== panelId);
      return [
        ...otherNotes,
        {
          notes,
          panelId,
          notesHasChanged,
        },
      ];
    });
  }, [notes, notesHasChanged, panelId, setCachedNotes]);

  return {
    notes: notes ?? cachedPanelNotes,
  };
};
